"use client";

import { useEffect } from "react";
import {
  clearBrowserAttributionStorage,
  parseGaClientId,
  readAdvertisingConsent,
  readAnalyticsConsent,
  readCookieValue,
} from "@/components/analytics/browser-attribution";
import { subscribeToBrowserConsentCategory } from "@/components/analytics/browser-consent-lifecycle";
import {
  buildSignupAttributionFromSearch,
  SIGNUP_ATTRIBUTION_STORAGE_KEY,
  SIGNUP_LAST_ATTRIBUTION_STORAGE_KEY,
} from "@/lib/signup-attribution";
import {
  isAnalyticsSuppressedUrl,
  isSuppressedUrlString,
} from "@/lib/analytics/suppressed-paths";

const VISITOR_ID_STORAGE_KEY = "tsb_attribution_visitor_id";

interface UtmCaptureProps {
  claimReferral?: boolean;
}

function createVisitorId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `visitor_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

function getVisitorId(): string {
  const existing = localStorage.getItem(VISITOR_ID_STORAGE_KEY);
  if (existing) return existing;
  const visitorId = createVisitorId();
  localStorage.setItem(VISITOR_ID_STORAGE_KEY, visitorId);
  return visitorId;
}

function withAnalyticsIds(
  attribution: Partial<Record<string, string>> | null,
  gaClientId: string | null,
  posthogAnonId: string | null,
): Record<string, string> | null {
  if (!attribution) return null;
  return {
    ...attribution,
    ...(gaClientId ? { gaClientId } : {}),
    ...(posthogAnonId ? { posthogAnonId } : {}),
  };
}

export function UtmCapture({ claimReferral = false }: UtmCaptureProps = {}) {
  useEffect(() => {
    let capturedForPage = false;

    function captureConsentedAttribution() {
      if (capturedForPage || readAdvertisingConsent() !== "accepted") {
        return;
      }
      capturedForPage = true;

      try {
        const params = new URLSearchParams(window.location.search);

        // Referral and campaign storage is optional attribution. It follows the
        // same accepted-cookie gate as click IDs and the durable capture API.
        const refCode = params.get("ref");
        if (refCode && !localStorage.getItem("tsb_referral_code")) {
          localStorage.setItem("tsb_referral_code", refCode);
        }

        // Claim referral code if stored from signup
        const storedRef = localStorage.getItem("tsb_referral_code");
        if (claimReferral && storedRef) {
          try {
            const request = fetch("/api/referrals/claim", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ referralCode: storedRef }),
            });
            Promise.resolve(request)
              .then((r) => {
                if (r.ok) localStorage.removeItem("tsb_referral_code");
              })
              .catch(() => {});
          } catch {
            // Attribution capture should not be blocked by referral claiming.
          }
        }

        const analyticsAccepted = readAnalyticsConsent() === "accepted";
        const gaClientId = analyticsAccepted
          ? parseGaClientId(readCookieValue("_ga"))
          : null;
        const posthogAnonId = analyticsAccepted
          ? readCookieValue("ph_anon_id")
          : null;
        const visitorId = getVisitorId();
        // Never capture a token-bearing landing page / source path / referrer
        // into attribution — it flows to Clerk unsafeMetadata, the signup Slack
        // alert, and the attribution DB. Scrub when the current URL (or the
        // referrer) is a bearer-token surface. Marketing params (utm/gclid/ref)
        // are read from `params` separately, so they're unaffected.
        const suppressed = isAnalyticsSuppressedUrl(
          window.location.pathname,
          window.location.search,
          window.location.hash,
        );
        const rawReferrer = document.referrer || null;
        const referrer = isSuppressedUrlString(rawReferrer)
          ? null
          : rawReferrer;
        const landingPage = suppressed
          ? ""
          : `${window.location.pathname}${window.location.search}`;
        const sourcePath = suppressed ? "" : window.location.pathname;
        const attribution = withAnalyticsIds(
          buildSignupAttributionFromSearch(
            params,
            referrer,
            landingPage,
            sourcePath,
            new Date().toISOString(),
            visitorId,
          ),
          gaClientId,
          posthogAnonId,
        );

        // First-touch attribution — don't overwrite campaign values if already
        // set, but fill analytics IDs once their cookies become available.
        const existingAttribution = localStorage.getItem(
          SIGNUP_ATTRIBUTION_STORAGE_KEY,
        );
        if (existingAttribution) {
          try {
            const parsed = JSON.parse(existingAttribution) as Record<
              string,
              unknown
            >;
            let changed = false;
            if (gaClientId && !parsed.gaClientId) {
              parsed.gaClientId = gaClientId;
              changed = true;
            }
            if (posthogAnonId && !parsed.posthogAnonId) {
              parsed.posthogAnonId = posthogAnonId;
              changed = true;
            }
            if (!parsed.visitorId) {
              parsed.visitorId = visitorId;
              changed = true;
            }
            if (changed) {
              localStorage.setItem(
                SIGNUP_ATTRIBUTION_STORAGE_KEY,
                JSON.stringify(parsed),
              );
            }
          } catch {
            // Leave malformed attribution alone; later flows will ignore it.
          }
        }

        // Only store if there's at least a campaign/ad param, ref code, or a referrer.
        if (!attribution) return;

        if (!existingAttribution) {
          localStorage.setItem(
            SIGNUP_ATTRIBUTION_STORAGE_KEY,
            JSON.stringify(attribution),
          );
        }
        localStorage.setItem(
          SIGNUP_LAST_ATTRIBUTION_STORAGE_KEY,
          JSON.stringify(attribution),
        );

        const request = fetch("/api/attribution/capture", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ visitorId, attribution }),
        });
        Promise.resolve(request).catch(() => {});
      } catch {
        // localStorage may be unavailable (private browsing, etc.)
      }
    }

    function onConsentChange() {
      // Re-read the cookie instead of trusting event detail so conflicting
      // path-scoped cookies fail closed exactly as they do on the server.
      if (readAdvertisingConsent() === "accepted") {
        captureConsentedAttribution();
      } else {
        capturedForPage = false;
        clearBrowserAttributionStorage();
      }
    }

    // Old attribution must not survive after consent is absent or withdrawn.
    if (readAdvertisingConsent() !== "accepted") {
      clearBrowserAttributionStorage();
    }
    captureConsentedAttribution();
    return subscribeToBrowserConsentCategory("advertising", onConsentChange);
  }, [claimReferral]);

  return null;
}
